import { useState, type Dispatch, type SetStateAction, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import client from '@/api/client';
import type { Complaint, ComplaintCategory } from '@/api/types';
import {
  AlertCircle, Plus, X, Clock, CheckCircle2, XCircle, ChevronDown, ChevronUp,
} from 'lucide-react';
import { Select } from '@/components/ui/Select';
import { useTenantPortalGuard, HostelSelector } from './tenantPortalShared';

type TFunc = ReturnType<typeof useTranslation>['t'];

const ACTIVE_STATUSES = new Set(['OPEN', 'IN_PROGRESS']);

const COMPLAINT_STATUS_COLORS: Record<string, string> = {
  OPEN:        'bg-red-100 text-red-700',
  IN_PROGRESS: 'bg-amber-100 text-amber-800',
  RESOLVED:    'bg-green-100 text-green-800',
  CANCELLED:   'bg-orange-100 text-orange-700',
};

const COMPLAINT_CARD_STYLE: Record<string, { border: string; icon: ReactNode; iconBg: string }> = {
  OPEN:        { border: 'border-l-red-400',    iconBg: 'bg-red-50',    icon: <AlertCircle  size={14} className="text-red-500"    /> },
  IN_PROGRESS: { border: 'border-l-amber-400',  iconBg: 'bg-amber-50',  icon: <Clock        size={14} className="text-amber-500"  /> },
  RESOLVED:    { border: 'border-l-green-400',  iconBg: 'bg-green-50',  icon: <CheckCircle2 size={14} className="text-green-600"  /> },
  CANCELLED:   { border: 'border-l-orange-300', iconBg: 'bg-orange-50', icon: <XCircle      size={14} className="text-orange-400" /> },
};

function getComplaintStatusLabels(t: TFunc): Record<string, string> {
  return {
    OPEN:        t('complaint.open'),
    IN_PROGRESS: t('complaint.inProgress'),
    RESOLVED:    t('complaint.resolved'),
    CANCELLED:   t('common.cancelled'),
  };
}

function getCategoryLabels(t: TFunc): Record<ComplaintCategory, string> {
  return {
    ROOM:              t('complaint.categoryRoom', 'Room'),
    CORRIDOR:          t('complaint.categoryCorridor', 'Corridor / Common Area'),
    WIFI:              t('complaint.categoryWifi', 'WiFi / Internet'),
    ELECTRICITY_WATER: t('complaint.categoryElectricityWater', 'Electricity / Water'),
    OTHER:             t('complaint.categoryOther', 'Other'),
  };
}

function ComplaintCard({ c }: { c: Complaint }) {
  const { t } = useTranslation();
  const style = COMPLAINT_CARD_STYLE[c.status] ?? COMPLAINT_CARD_STYLE['CANCELLED'];
  const isDone = !ACTIVE_STATUSES.has(c.status);
  return (
    <div className={`border border-l-4 ${style.border} border-gray-100 rounded-xl p-4 ${isDone ? 'bg-gray-50/60 opacity-80' : 'bg-white'}`}>
      <div className="flex items-start gap-3">
        <div className={`mt-0.5 w-7 h-7 rounded-lg ${style.iconBg} flex items-center justify-center shrink-0`}>
          {style.icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{getCategoryLabels(t)[c.category]}</span>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${COMPLAINT_STATUS_COLORS[c.status]}`}>
              {getComplaintStatusLabels(t)[c.status]}
            </span>
            <span className="text-xs text-gray-400 ml-auto">{new Date(c.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
          </div>
          <p className={`font-semibold text-sm ${isDone ? 'text-gray-500' : 'text-gray-900'}`}>{c.title}</p>
          <p className="text-xs text-gray-400 mt-0.5">{c.description}</p>
          {c.resolutionNotes && (
            <div className="mt-2 flex items-start gap-1.5 bg-green-50 border border-green-100 rounded-lg px-3 py-2">
              <CheckCircle2 size={12} className="text-green-600 mt-0.5 shrink-0" />
              <p className="text-xs text-green-800">{c.resolutionNotes}</p>
            </div>
          )}
          {c.resolvedAt && (
            <p className="text-[10px] text-gray-400 mt-1">
              {t('tenant.statusOnDate', '{{status}} on {{date}}', {
                status: c.status === 'RESOLVED' ? t('complaint.resolved') : t('complaint.closed'),
                date: new Date(c.resolvedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
              })}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

interface ComplaintsPanelProps {
  complaints: Complaint[];
  showForm: boolean;
  setShowForm: Dispatch<SetStateAction<boolean>>;
  form: { category: ComplaintCategory; title: string; description: string };
  setForm: Dispatch<SetStateAction<{ category: ComplaintCategory; title: string; description: string }>>;
  onSubmit: () => void;
  isSubmitting: boolean;
  submitError?: string;
  canSubmit: boolean;
}

function ComplaintsPanel({ complaints, showForm, setShowForm, form, setForm, onSubmit, isSubmitting, submitError, canSubmit }: ComplaintsPanelProps) {
  const { t } = useTranslation();
  const [showHistory, setShowHistory] = useState(false);
  const active  = complaints.filter(c => ACTIVE_STATUSES.has(c.status));
  const history = complaints.filter(c => !ACTIVE_STATUSES.has(c.status));

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-gray-700">
          {active.length > 0
            ? t('tenant.activeIssuesCount', '{{count}} Active Issue{{s}}', { count: active.length, s: active.length !== 1 ? 's' : '' })
            : t('tenant.noActiveIssues', 'No active issues')}
        </p>
        <button
          onClick={() => setShowForm(v => !v)}
          className="flex items-center gap-1.5 text-sm bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 rounded-lg font-medium transition"
        >
          {showForm ? <><X size={14} /> {t('common.cancel')}</> : <><Plus size={14} /> {t('complaint.raiseComplaint', 'Raise Complaint')}</>}
        </button>
      </div>

      {showForm && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
          <p className="text-sm font-semibold text-amber-900">{t('complaint.newComplaint', 'New Complaint')}</p>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('complaint.category')}</label>
            <Select
              value={form.category}
              onChange={e => setForm(f => ({ ...f, category: e.target.value as ComplaintCategory }))}
              options={(Object.entries(getCategoryLabels(t)) as [ComplaintCategory, string][]).map(([k, v]) => ({ value: k, label: v }))}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('complaint.titleLabel', 'Title')} *</label>
            <input
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder={t('complaint.titlePlaceholder', 'e.g. Tap leaking in bathroom')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('complaint.description')} *</label>
            <textarea
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder={t('complaint.descriptionPlaceholder', 'Describe the issue in detail…')}
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onSubmit}
              disabled={isSubmitting || !canSubmit}
              className="text-sm bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50"
            >
              {isSubmitting ? t('common.submitting', 'Submitting…') : t('common.submit')}
            </button>
            {submitError && <p className="text-red-600 text-sm">{submitError}</p>}
          </div>
        </div>
      )}

      {active.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-8 border-2 border-dashed border-gray-100 rounded-xl">
          <CheckCircle2 size={32} className="text-green-400" />
          <p className="text-sm font-medium text-gray-500">{t('complaint.allClear', 'All clear — no open issues')}</p>
        </div>
      ) : (
        <div className="space-y-2">{active.map(c => <ComplaintCard key={c.id} c={c} />)}</div>
      )}

      {history.length > 0 && (
        <div>
          <button
            onClick={() => setShowHistory(v => !v)}
            className="flex items-center gap-2 w-full text-left py-2 border-t border-gray-100"
          >
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide flex-1">
              {t('complaint.historyResolvedCount', 'History · {{count}} resolved', { count: history.length })}
            </span>
            {showHistory ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
          </button>
          {showHistory && (
            <div className="space-y-2 mt-2">
              {history.map(c => <ComplaintCard key={c.id} c={c} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function TenantComplaintsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const portal = useTenantPortalGuard();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState({ category: 'ROOM' as ComplaintCategory, title: '', description: '' });

  const { data: complaints = [], isLoading: complaintsLoading } = useQuery<Complaint[]>({
    queryKey: ['tenant-portal-complaints'],
    queryFn: () => client.get('/api/tenant-portal/complaints').then(r => r as unknown as Complaint[]),
  });

  const submitComplaint = useMutation({
    mutationFn: () => client.post('/api/complaints', form),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tenant-portal-complaints'] });
      setShowForm(false);
      setForm({ category: 'ROOM', title: '', description: '' });
    },
  });

  if ('guard' in portal) return portal.guard;
  const { tenant, activeTenants, tenantIdx, setSelectedTenantIdx } = portal;

  const tenantComplaints = complaints.filter(c => c.tenantId === tenant.id);

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <h1 className="text-2xl font-bold text-gray-900">{t('nav.complaints')}</h1>

      <HostelSelector activeTenants={activeTenants} tenantIdx={tenantIdx} onChange={setSelectedTenantIdx} />

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        {complaintsLoading ? (
          <div className="p-6 text-center">
            <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-gray-400 text-sm">{t('common.loading')}</p>
          </div>
        ) : (
          <ComplaintsPanel
            complaints={tenantComplaints}
            showForm={showForm}
            setShowForm={setShowForm}
            form={form}
            setForm={setForm}
            onSubmit={() => submitComplaint.mutate()}
            isSubmitting={submitComplaint.isPending}
            submitError={submitComplaint.isError ? (submitComplaint.error as Error).message : undefined}
            canSubmit={!!form.title.trim() && !!form.description.trim()}
          />
        )}
      </div>
    </div>
  );
}
